import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { useAlerts } from '../../hooks/useAlerts';
import AlertList from '../../components/alerts/AlertList';
import Spinner from '../../components/ui/Spinner';
import Card from '../../components/ui/Card';
import clsx from 'clsx';

export default function AlertsPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<'active' | 'acknowledged' | 'resolved' | undefined>('active');
  const [severityFilter, setSeverityFilter] = useState<'warning' | 'critical' | undefined>(undefined);

  const { data, isLoading } = useAlerts({
    status: statusFilter,
    severity: severityFilter,
    limit: 50,
  });

  const filterOptions = [
    { value: 'active', label: t('alerts.filters.active') },
    { value: 'acknowledged', label: t('alerts.filters.acknowledged') },
    { value: 'resolved', label: t('alerts.filters.resolved') },
    { value: undefined, label: t('alerts.filters.all') },
  ];

  const severityOptions = [
    { value: undefined, label: t('alerts.filters.allSeverities') },
    { value: 'critical', label: t('alerts.filters.critical') },
    { value: 'warning', label: t('alerts.filters.warning') },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">{t('alerts.title')}</h1>
      </div>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('alerts.stats.active')}</p>
                <p className="text-2xl font-bold text-gray-900">{data.active_count}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('alerts.stats.critical')}</p>
                <p className="text-2xl font-bold text-gray-900">{data.critical_count}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('alerts.stats.total')}</p>
                <p className="text-2xl font-bold text-gray-900">{data.total}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('alerts.filters.status')}
          </label>
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => (
              <button
                key={String(option.value)}
                onClick={() => setStatusFilter(option.value as any)}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                  statusFilter === option.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('alerts.filters.severity')}
          </label>
          <div className="flex flex-wrap gap-2">
            {severityOptions.map((option) => (
              <button
                key={String(option.value)}
                onClick={() => setSeverityFilter(option.value as any)}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                  severityFilter === option.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {data && <AlertList alerts={data.items} />}
    </div>
  );
}
